import { isNotEmpty } from "../../../utils/validation"
import { METROPOLITAN_FRANCE_AREA_CODE, BIKE_SOLD, OWNER_PRIVATE_GENDER, OWNER_COMPANY_GENDER, getBrandValueFromLabel, getIdFromBikeLabel } from "../../../utils/constants"
import { v4 as uuidv4 } from "uuid"
import { DefaultRowFromTemplateProps } from "../BikeCollectionImport"
import { GetBikeOwnerInfoMapperProps } from "../../../models"

// Fix Buffer is not defined
// eslint-disable-next-line @typescript-eslint/no-var-requires
window.Buffer = window.Buffer || require("buffer").Buffer
/*
The idea is to gather the data filled by the user in the imported xlsx file,
and then to generate for each row the expected bike object
----------
Correlation table:
----------
| XLSX column index | XLSX column text header | Value origin                     | Value target                                 |
|-------------------|-------------------------|----------------------------------|----------------------------------------------|
| 0                 | ID Fnuci                | From xlsx file                   | bike_id                                      |
| 1                 | Type                    | From xlsx file                   | bike_description.gear_type                   |
| -                 | -                       | calculated by string type value  | bike_description.gear_type_id                |
| 2                 | Marque                  | From xlsx file                   | bike_description.brand                       |
| -                 | -                       | Calculated by string brand value | bike_description.brand_id                    |
| 3                 | Modèle                  | From xlsx file                   | bike_description.model                       |
| 4                 | Couleur                 | From xlsx file                   | bike_description.color                       |
| 5                 | VAE                     | From xlsx file                   | bike_description.is_electric                 |
| 6                 | Cadre                   | From xlsx file                   | bike_description.frame_serial_number         |
| 7                 | Moteur                  | From xlsx file                   | bike_description.engine_serial_number        |
| 8                 | Batterie                | From xlsx file                   | bike_description.battery_serial_number       |
| -                 | -                       | 2                                | bike_owner.private_owner.gender              |
| 9                 | Nom                     | From xlsx file                   | bike_owner.private_owner.last_name           |
| 10                | Prénom                  | From xlsx file                   | bike_owner.private_owner.first_name          |
| 11                | Téléphone               | From xlsx file                   | bike_owner.private_owner.phone               |
| 12                | Mail                    | From xlsx file                   | bike_owner.private_owner.mail                |
| -                 | -                       | 1                                | bike_owner.company_owner.gender              |
| 13                | Raison sociale          | From xlsx file                   | bike_owner.company_owner.social_reason       |
| 14                | Téléphone               | From xlsx file                   | bike_owner.company_owner.phone               |
| 15                | Mail                    | From xlsx file                   | bike_owner.company_owner.mail                |
| 16                | Numéro de compte client | From xlsx file                   | bike_owner.owner_by_id.consumer_id           |
| -                 | -                       | 1                                | bike_status                                  |
| -                 | -                       | "default"                        | send_notification_by_mail                    |
| -                 | -                       | "default"                        | send_receipt_by_mail                         |
|-------------------|-------------------------|----------------------------------|----------------------------------------------|
 */


export const mapRowsFileToBikeCollectionObj = (bikeCollection) => {
    return bikeCollection.map((bike) => {
      const mandatoryInfoData = getMandatoryBikeInfoMapper(bike)
      const optionalSerialNumbersData = getOptionalSerialNumbersMapper(bike)
      const commonBikeInfoData = {
        ...mandatoryInfoData,
        bike_description: {
          ...mandatoryInfoData.bike_description,
          ...(optionalSerialNumbersData ?? {})
        }
      }
      const bikeOwnerInfoData: GetBikeOwnerInfoMapperProps = getBikeOwnerInfoMapper(bike)

      return { ...commonBikeInfoData, ...bikeOwnerInfoData }
    })
}

const createPhoneNumber = (phone: string) => {
  // Request made by the client: all phonenumber starting with a 0 should be
  // converted to +33, even if it is not a metropolitan one
  if(phone.charAt(0) === "0") return `${METROPOLITAN_FRANCE_AREA_CODE}${phone.substring(1)}`
  return phone
}

/*
 * Generic bike informations mapper
 */
const getMandatoryBikeInfoMapper = (bike: DefaultRowFromTemplateProps) => {
  const isEletric = ["OUI", "YES", "JA", "SI"].includes(bike.is_electric?.toUpperCase())
  return {
    id: uuidv4(), // We have to use a technical id because the bike_id can be empty/null
    bike_id: bike?.bike_id?.trim(),
    bike_status: BIKE_SOLD,
    bike_description: {
      gear_type_id: getIdFromBikeLabel(bike.gear_type?.trim()),
      gear_type: bike.gear_type?.trim(),
      brand_id: getBrandValueFromLabel(bike.brand?.trim()),
      brand: bike.brand?.trim(),
      model: bike.model?.trim(),
      color: bike.color?.trim(),
      is_electric: isEletric,
    },
    send_notification_by_mail: "default",
    send_receipt_by_mail: "default"
  }
}

/*
 * Serial numbers mapper
 */
const getOptionalSerialNumbersMapper = (bike: DefaultRowFromTemplateProps) => {
  return {
    ...(isNotEmpty(bike?.frame_serial_number) ? { "frame_serial_number": bike?.frame_serial_number.toString() } : {}),
    ...(isNotEmpty(bike?.engine_serial_number) ? { "engine_serial_number": bike?.engine_serial_number.toString() } : {}),
    ...(isNotEmpty(bike?.battery_serial_number) ? { "battery_serial_number": bike?.battery_serial_number.toString() } : {}),
  }
}

/*
 * Information about the owner associated with the bike mapper
 */

const getBikeOwnerInfoMapper = (bike: DefaultRowFromTemplateProps) => {
  return {
    bike_owner: {
      company_owner: {
        gender: OWNER_COMPANY_GENDER,
        social_reason: bike.social_reason?.trim(),
        phone: bike.company_owner_phone ? createPhoneNumber(bike.company_owner_phone?.toString().trim()) : null,
        mail: bike.company_owner_mail?.trim(),
      },
      private_owner: {
        gender: OWNER_PRIVATE_GENDER,
        last_name: bike.last_name?.trim(),
        first_name: bike.first_name?.trim(),
        phone: bike.private_owner_phone ? createPhoneNumber(bike.private_owner_phone?.toString().trim()) : null,
        mail: bike.private_owner_mail?.trim(),
      },
      owner_by_id: {
        consumer_id: bike.bike_oconsumer_id?.toString().trim()
      }
    }
  }
}